import { ApiError } from '../identity/types';
import { classifyFailure } from '../pay-employees/safeErrors';

/**
 * The screens never render `err.message`. Every failure is reduced to a fixed, plain-language sentence, chosen by the server's stable `code` first and by the
 * HTTP class otherwise.
 */
export interface RecordFailure {
  message: string;
  /** 409 DUPLICATE_PAYMENT_RECORD: the admin may confirm "record anyway". */
  duplicate: boolean;
  notFound: boolean;
  /** 403 / 401 / 503: the situation is not about the values typed. */
  kind: 'validation' | 'forbidden' | 'session' | 'unavailable' | 'notFound' | 'conflict' | 'other';
}

const CODE_COPY: Record<string, string> = {
  INVALID_STATUS_TRANSITION: 'This payment record can’t move to that status from its current status. Refresh the page to see its latest status.',
  DUPLICATE_PAYMENT_RECORD: 'A payment with the same contractor, date, amount and reference is already recorded.',
  EXPORT_TOO_LARGE: 'Too many records to export at once. Narrow the filters and try again.',
  FEATURE_NOT_AVAILABLE_IN_PRODUCTION: 'This action is not available in this release.',
};

export function describeRecordFailure(err: unknown, fallback: string): RecordFailure {
  if (err instanceof ApiError) {
    const known = err.code ? CODE_COPY[err.code] : undefined;
    if (known) return { message: known, duplicate: err.code === 'DUPLICATE_PAYMENT_RECORD', notFound: false, kind: 'conflict' };
    if (err.status === 400) return { message: 'Check the values you entered. A required value is missing or not valid.', duplicate: false, notFound: false, kind: 'validation' };
    if (err.status === 404) return { message: 'That record could not be found. Refresh the page and try again.', duplicate: false, notFound: true, kind: 'notFound' };
    if (err.status === 409) return { message: 'This record changed or already exists. Refresh the page and try again.', duplicate: false, notFound: false, kind: 'conflict' };
  }
  switch (classifyFailure(err)) {
    case 'forbidden':
      return { message: 'You don’t have permission to do this. Ask an administrator to grant you access.', duplicate: false, notFound: false, kind: 'forbidden' };
    case 'session':
      return { message: 'Your session may have expired. Sign in again, then retry.', duplicate: false, notFound: false, kind: 'session' };
    case 'unavailable':
      return { message: 'A connected service isn’t responding right now. Try again shortly.', duplicate: false, notFound: false, kind: 'unavailable' };
    default:
      return { message: fallback, duplicate: false, notFound: false, kind: 'other' };
  }
}
